<template>
  <div :id="id + '_fs'" :class="_fs_class">
    <cat-label
      :label-id="id + '_lbl'"
      :for-id="textboxId"
      :label="label"
      :optional="!mandatory"
      :placeholder="offscreenLabel"
      :errors="errors"
      :readonly="readonly"
      :display-optional="showOptionals"
    ></cat-label>
    <cat-hint :id="id + '_hint'" :text="hint" />
    <slot></slot>
    <div ref="selectholder" :class="_holder_class">
      <span class="dropdown-indicator" tabindex="-1" aria-hidden="true"></span>
      <select
        :id="id"
        ref="selectElement"
        v-model="model"
        :title="label"
        :aria-required="mandatory"
        :required="errors && errors.length > 0"
        :aria-invalid="errors && errors.length > 0"
        :readonly="readonly"
        :disabled="readonly"
        :aria-labelledby="label ? null : id + '_lbl'"
        :aria-describedby="hint ? id + '_hint' : null"
        data-selectized="true"
        @change="_onChange"
        @blur="_onBlur"
      >
        <option
          v-for="(item, idx) in items"
          :key="idx"
          :value="item.value"
          :selected="item.value == value ? 'selected' : ''"
        >
          {{ item.text }}
        </option>
      </select>
    </div>
    <slot name="after-input"></slot>
    <cat-errors v-if="errors && errors.length > 0" :errors="errors" />
    <cat-errors v-if="messages && messages.length > 0" :errors="messages" />
    <div role="presentation" class="spacer"></div>
  </div>
</template>

<script lang="ts">
import jQuery from "jquery";

import "@/plugins/selectize/plugin_a11y.js";
import "@/plugins/selectize/auto_position.js";

import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import CatErrors from "@/components/catErrors/catErrors.vue";
import CatLabel from "@/components/utilities/cat-label.vue";
import CatHint from "@/components/utilities/hint.vue";
import SelectMixin from "./select-mixin.js";
import SelectizeMixin from "./selectize-mixin.js";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);
    const displayParams = inject("displayParams", false);
    const showOptionals = inject("showOptionals", true);

    return {
      resolveTxt,
      displayParams,
      showOptionals
    };
  },
  name: "CatAttributeSelectRemote",
  components: {
    CatErrors,
    CatLabel,
    CatHint
  },
  mixins: [SelectMixin, SelectizeMixin],
  props: {
    id: [String],
    errors: [Array],
    label: [String],
    items: [Array],
    hint: [String],
    placeholder: [String],
    offscreenLabel: [String],
    value: [String],
    autofillToken: {
      type: String,
      default: "off",
      description:
        "The autofill token to be set on the input text element in the multi select."
    },
    highlighttop: [Boolean],
    filterOptions: {
      type: Boolean,
      default: false,
      description:
        "Set to true if the displayed text in the dropdown options is be filtered as matching by the typed in value the text field. This works in the assumption that the options coming in from the server are already from the search results."
    },
    mandatory: {
      type: Boolean,
      default: false
    },
    clearOptions: {
      type: Boolean,
      default: true
    },
    size: [String],
    readonly: [Boolean],
    autocompleteFunction: [Function],
    autocompleteSearchingText: [String],
    autocompleteNoResultsText: [String],
    minInputLength: {
      type: Number,
      default: 2
    },
    showLines: { type: Boolean, default: false },
    hideSelected: { type: Boolean, default: false },
    openOnClick: { type: Boolean, default: true },
    options: {
      type: [Object],
      default: function () {
        return {};
      }
    }
  },
  data: function () {
    return {
      model: this.value ? this.value : "",
      selectize: null,
      textboxId: this.id + "-select-textbox",
      selectItemId: this.id + "-select-item",
      messages: null
    };
  },
  computed: {
    _fs_class: function () {
      return {
        fieldset: true,
        "cat-select-remote": true,
        "cat-select-autosuggest": true,
        "autosuggest-remote": true,
        "type-to-open-options": this.minInputLength > 0,
        required: this.mandatory,
        optional: !this.mandatory,
        "readonly-list": this.readonly,
        "autosuggest-select": this.minInputLength < 1 || this.openOnClick,
        "highlight-top": this.highlighttop
      };
    },
    _holder_class: function () {
      return {
        // 'select-holder': !this.autocomplete,
        "ac-holder": true,
        small: this.size == "small",
        medium: !this.size || this.size == "medium",
        large: this.size == "large"
      };
    }
  },
  watch: {
    value: function (newval, oldvalue) {
      if (this.model != newval) {
        this.model = newval;
        this.selectize.setValue(newval);
      }
    }
  },

  mounted: function () {
    // const autocompleteFunction = this.autocompleteFunction
    const _this = this;
    let settings = {
      plugins: {
        selectize_a11y: {
          openOnClick: this.openOnClick,
          textboxId: this.textboxId,
          selectItemIds: [this.selectItemId],
          autofillToken: this.autofillToken,
          label: this.label || this.offscreenLabel || this.placeholder,
          labelId: this.label ? this.id + "_lbl" : null,
          id: this.id,
          forceActiveDescendant: this.hideSelected // Enable forceActiveDescendant to plugin for accessibility
        },
        auto_position: true
      },
      load: (query, callback) => {
        if (query.length < this.minInputLength) {
          this.optionsCleanUp();
          return callback();
        }
        const res = this.autocompleteFunction(query);
        if (_this.clearOptions) {
          _this.selectize.clearOptions(true);
        }
        res.then((data) => {
          callback(data.results);
          if (data.messages && data.messages.length > 0) {
            _this.messages = data.messages;
          } else {
            _this.messages = null;
          }
          if (!data.results || data.results.length === 0) {
            this.optionsCleanUp();
          }
        });
      },
      onLoad: function (data) {
        // The options in the drop down does not show all the lines (for multi-lines)
        // if a default value is passed into the selectize
        // This can be solve by the having the 'options' settings on initialise
        // But due to the asynchronous call to get all the options it is not ideal
        if (
          _this.showLines &&
          _this.value &&
          _this.value === _this.selectize.getValue()
        ) {
          data.forEach((d) => {
            if (d.id === _this.value) {
              _this.selectize.updateOption(d.id, d);
            }
          });
        }
      },
      onChange: (val) => {
        const options = _this.selectize.options;
        const text = options[val] ? options[val].text : null;
        if (val === "") {
          _this.selectize.clearOptions();
          _this.autocompleteClear();
        }
        if (text) {
          _this.autocompleteChange(val, text);
        } else {
          _this.autocompleteCreate(val);
        }
      },
      onClear: () => {},
      score: function (search) {
        var score = this.getScoreFunction(search);
        return function (item) {
          if (_this.filterOptions) {
            return score(item);
          } else {
            // Do not filter out the drop-down options
            // Always a match (greater than 0).
            return 1 + score(item);
          }
        };
      },
      render: {
        item: _this.showLines
          ? _this.createOptionRow
          : function (item, escape) {
              return (
                '<div class="option"><span class="option-row" id="' +
                this.selectItemId +
                '"><span class="line caption">' +
                escape(item.text) +
                "</span></span></div>"
              );
            },
        option: function (item, escape) {
          return _this.createOptionRow(item, escape);
        }
      },
      valueField: "id",
      labelField: "text",
      create: false,
      createOnBlur: true,
      persist: false,
      maxItems: 1,
      hideSelected: this.hideSelected,
      openOnFocus: false,
      placeholder: this.placeholder,
      preload: this.minInputLength === 0
    };

    let plgs = {};
    //the core 'settings'
    plgs = jQuery.extend(plgs, settings.plugins);
    //if passed in 'options' contain 'plugins' merge it in to 'plgs'
    if (
      _this.options.plugins &&
      Object.keys(_this.options.plugins).length > 0 &&
      _this.options.plugins.constructor === Object
    ) {
      plgs = jQuery.extend({}, plgs, _this.options.plugins);
    }
    //merge all 'options' and 'settings'
    settings = jQuery.extend(settings, _this.options);
    //now override just 'plugins' with the merged ones 'plgs'
    settings.plugins = plgs;

    const s = jQuery(this.$refs.selectElement).selectize(settings);
    if (s && s.length > 0) {
      this.selectize = s[0].selectize;
    }
  },
  methods: {
    _onBlur: function (event) {
      this.$emit("ui-blur", {
        event: event,
        id: this.id
      });
    },
    autocompleteChange: function (value, text) {
      // console.log("chg", value, text);
      if (value == this.model) {
        return;
      }
      this.model = value;
      this._onChange(value, text);
    },
    autocompleteCreate: function (text) {
      // console.log("create", text);
      if (text == this.model) {
        return;
      }

      this.$emit("ui-create", {
        id: this.id ? this.id : "",
        text: text,
        el: this.$el,
        value: text
      });
    },
    autocompleteClear: function () {
      this.$emit("ui-clear", {
        id: this.id ? this.id : "",
        el: this.$el
      });
    },
    createOptionRow: function (item, escape) {
      var label = item.text;
      var avatar = item.avatar && item.avatar !== "" ? item.avatar : false;
      var extraLines = item.lines && item.lines.length > 0 ? item.lines : false;
      var optionId = this._formatOptionId(label);
      var optionRow =
        '<div class="option" role="option" id="' + optionId + '">';

      if (avatar) {
        optionRow +=
          '<div class="option-avatar" role="option" id="' + optionId + '">';
        optionRow +=
          '<span class="avatar mrt"><i class="icon icon-' +
          avatar +
          '"></i></span>';
      }

      optionRow += '<span class="option-row">';
      optionRow += `<span class="line ${
        extraLines && extraLines.length > 0 ? "caption" : ""
      }">${escape(label)}</span>`;

      if (extraLines) {
        for (var i = 0; i < extraLines.length; i++) {
          optionRow +=
            '<span class="line line-' +
            i +
            '">' +
            escape(extraLines[i]) +
            "</span>";
        }
      }

      if (avatar) {
        optionRow += "</div>";
      }

      optionRow += "</span></div>";
      return optionRow;
    },
    // Selectize keeps the dropdown open even if there are no results, unless you explicitly close it.
    optionsCleanUp: function () {
      this.selectize.clearOptions(true);
      this.selectize.clear(true);
      this.selectize.close();
    }
  }
});
</script>
